import _ from 'lodash';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  BenchmarkTutorialResponse,
  FieldInstruction,
} from '@scale/shared/types/benchmarks/tutorial';
import { InteractionStepType } from '@scale/shared/types/chat';
import { BenchmarkTutorialMode } from '@scale/shared/qm/qmRequestTypes';
import { SendTypes } from 'react-scale/components/scalers/tasks/common/SendTask';

/**
 * Zustand store for tutorial state
 *
 * Note: (jonathan.wang) Due to the need for things like backfilling, the tutorial store
 *   and underlying tutorialResponse types are quite complex, making the parsing/drilling of responses
 *   /data very annoying. Be careful to do nully checks and error handling or you may crash the page.
 */

export enum TutorialMessageType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
}

export type TutorialHighlightType = {
  highlightStrings: string[];
};

type GetTutorialResponseReturnType = {
  message: string;
  messageType: TutorialMessageType;
  highlight?: TutorialHighlightType;
};

type TutorialResponseType = { responses: BenchmarkTutorialResponse[] };

type TutorialStoreType = {
  /**
   * Response objects tracing to TrainingScenario
   */
  tutorialResponse: TutorialResponseType | undefined;
  expectedResponse: $FIXME | undefined;
  tutorialMode: BenchmarkTutorialMode;
  canSubmitScenario: boolean;

  /**
   * Setters on first load of assignment
   */
  setTutorialResponse: (tutorialResponse: TutorialResponseType | undefined) => void;
  setExpectedResponse: (expectedResponse: $FIXME) => void;
  setTutorialMode: (tutorialMode: BenchmarkTutorialMode) => void;

  /**
   * Highlight states and setters
   */
  highlightStrings: string[] | undefined;
  highlightActive: boolean;
  highlightFieldId: string | undefined;
  highlightType: TutorialMessageType | undefined;
  setHighlightStrings: (highlightStrings: string[] | undefined) => void;
  setHighlightActive: (highlightActive: boolean) => void;
  setHighlightFieldId: (highlightFieldId: string | undefined) => void;
  setHighlightType: (highlightType: TutorialMessageType | undefined) => void;

  // Returns either message/type or null for bad responses
  getTutorialResponseForField: (
    fieldId: string,
    stepIndex: number,
    response: $FIXME,
    candidateId?: string,
  ) => GetTutorialResponseReturnType | null;

  // Track if the CB has finished the scenario and attempted submitting
  setCanSubmitScenario: (canSubmitScenario: boolean) => void;

  // Track acknowledged tutorial fields
  acknowledgedTutorialFields: Record<string, boolean>;
  setAcknowledgedTutorialFields: (acknowledgedTutorialFields: Record<string, boolean>) => void;

  // Course Tutorial Benchmark
  canSubmitCourseBenchmark: boolean;
  setCanSubmitCourseBenchmark: (canSubmitCourseBenchmark: boolean) => void;
  handleSubmitCourseBenchmark:
    | ((
        sendType?: SendTypes,
        isReportedForSensitiveContent?: boolean,
        sensitiveContentReportReason?: string,
        sensitiveContentReportExplanation?: string,
      ) => Promise<void>)
    | undefined;
  setHandleSubmitCourseBenchmark: (
    handleSubmitCourseBenchmark:
      | ((
          sendType?: SendTypes,
          isReportedForSensitiveContent?: boolean,
          sensitiveContentReportReason?: string,
          sensitiveContentReportExplanation?: string,
        ) => Promise<void>)
      | undefined,
  ) => void;
};

const doesFieldHaveInstructions = ({
  fieldId,
  stepIndex,
  tutorialResponse,
  candidateId,
}: {
  fieldId: string;
  stepIndex: number;
  tutorialResponse: TutorialResponseType;
  candidateId?: string;
}) => {
  const step = tutorialResponse.responses[stepIndex];
  if (step.type === InteractionStepType.TextCollection) {
    const field = step.fieldInstructions[fieldId];
    return !!field;
  }
  // We guard against candidateId being undefined here to prevent index errors
  else if (step.type === InteractionStepType.TextCollectionPerResponse && candidateId) {
    const field = step.candidates[candidateId].fieldInstructions[fieldId];
    return !!field;
  } else if (
    step.type === InteractionStepType.ModelResponseSelector &&
    (fieldId === 'likertValueRaw' || fieldId === 'selectedIndex' || fieldId === 'justification')
  ) {
    const field = step.stepInstructions[fieldId];
    return !!field;
  } else if (step.type === InteractionStepType.QualityMeasurement) {
    // necessary to check for undefined fieldInstructions to prevent errors with old tutorial BMs not having fieldInstructions on QMS steps
    const field = step?.fieldInstructions?.[fieldId];
    return !!field;
  }

  // All other types
  return false;
};

const getTutorialMessageForField = ({
  fieldId,
  stepIndex,
  tutorialResponse,
  candidateId,
}: {
  fieldId: string;
  stepIndex: number;
  tutorialResponse: TutorialResponseType;
  candidateId?: string;
}) => {
  const step = tutorialResponse.responses[stepIndex];
  if (step.type === InteractionStepType.TextCollection) {
    const field = step.fieldInstructions[fieldId];
    return field.message.messageString;
  } else if (step.type === InteractionStepType.TextCollectionPerResponse && candidateId) {
    const field = step.candidates[candidateId].fieldInstructions[fieldId];
    return field.message.messageString;
  } else if (
    step.type === InteractionStepType.ModelResponseSelector &&
    (fieldId === 'likertValueRaw' || fieldId === 'selectedIndex' || fieldId === 'justification')
  ) {
    const field = step.stepInstructions[fieldId];
    return field?.message.messageString || '';
  } else if (step.type === InteractionStepType.QualityMeasurement) {
    // necessary to check for undefined fieldInstructions to prevent errors with old tutorial BMs not having fieldInstructions on QMS steps
    const field = step?.fieldInstructions?.[fieldId];
    return field?.message.messageString || '';
  }
  return '';
};

/**
 * Returns undefined if no highlight, otherwise returns highlight object
 */
const getTutorialHighlightForField = ({
  fieldId,
  stepIndex,
  tutorialResponse,
  candidateId,
}: {
  fieldId: string;
  stepIndex: number;
  tutorialResponse: TutorialResponseType;
  candidateId?: string;
}) => {
  const step = tutorialResponse.responses[stepIndex];
  if (step.type === InteractionStepType.TextCollection) {
    const { highlight } = step.fieldInstructions[fieldId];
    if (highlight) {
      return { highlightStrings: highlight.highlightStrings };
    }
    return undefined;
  } else if (step.type === InteractionStepType.TextCollectionPerResponse && candidateId) {
    const field = step.candidates[candidateId].fieldInstructions[fieldId];
    if (field && field.highlight) {
      return { highlightStrings: field.highlight.highlightStrings };
    }
    return undefined;
  } else if (
    step.type === InteractionStepType.ModelResponseSelector &&
    (fieldId === 'likertValueRaw' || fieldId === 'selectedIndex' || fieldId === 'justification')
  ) {
    const field = step.stepInstructions[fieldId];
    if (field && field.highlight) {
      return { highlightStrings: field.highlight.highlightStrings };
    }
    return undefined;
  } else if (step.type === InteractionStepType.QualityMeasurement) {
    // necessary to check for undefined fieldInstructions to prevent errors with old tutorial BMs not having fieldInstructions on QMS steps
    const field = step?.fieldInstructions?.[fieldId];
    if (field && field.highlight) {
      return { highlightStrings: field.highlight.highlightStrings };
    }
    return undefined;
  }
  return undefined;
};

/**
 * Returns type for tutorial message
 */
const getTutorialMessageTypeForField = ({
  fieldId,
  stepIndex,
  response,
  candidateId,
  expectedResponse,
  tutorialResponse,
}: {
  fieldId: string;
  stepIndex: number;
  response: $FIXME;
  candidateId?: string;
  expectedResponse: $FIXME;
  tutorialResponse: TutorialResponseType;
}) => {
  // Basic variables
  const step = tutorialResponse.responses[stepIndex];
  const stepInlineGrader =
    expectedResponse.responses[stepIndex].graderConfig?.gradedFields ?? undefined;

  // Temp variables used to extract later
  let field: FieldInstruction | undefined;
  let inlinePath: string | undefined;
  let expectedAtIndex: $FIXME; // typing honestly can be any because of how fields are

  // Extract the field and check if it is an info message
  try {
    if (step.type === InteractionStepType.TextCollection) {
      field = step.fieldInstructions[fieldId];
      inlinePath = `context.response.annotations.${fieldId}.response[0]`;
      expectedAtIndex =
        expectedResponse.responses[stepIndex].context.response.annotations[fieldId].response[0];
    }
    // TCPR is a bit more complex as we need to drill down into the candidateId
    else if (step.type === InteractionStepType.TextCollectionPerResponse && candidateId) {
      field = step.candidates[candidateId].fieldInstructions[fieldId];
      inlinePath = `context.annotations.["${candidateId}"].annotations.${fieldId}.response[0]`;
      expectedAtIndex =
        expectedResponse.responses[stepIndex].context.annotations[candidateId].annotations[fieldId]
          .response[0];
    }
    // MRS has an edge case where we only want to check on these static names
    else if (
      step.type === InteractionStepType.ModelResponseSelector &&
      (fieldId === 'likertValueRaw' || fieldId === 'selectedIndex')
    ) {
      field = step.stepInstructions[fieldId];
      inlinePath = `context.${fieldId}`;
      expectedAtIndex = expectedResponse.responses[stepIndex].context[fieldId];
    } else if (step.type === InteractionStepType.QualityMeasurement) {
      field = step.fieldInstructions[fieldId];
      inlinePath = `context.response.annotations.${fieldId}.response[0]`;
      expectedAtIndex =
        expectedResponse.responses[stepIndex].context.response.annotations[fieldId].response[0];
    }
    // Escape case: return info
    else {
      return TutorialMessageType.INFO;
    }
  } catch (e) {
    // If there is an error, return INFO
    return TutorialMessageType.INFO;
  }

  if (field && field.message.isInfo) {
    return TutorialMessageType.INFO;
  }

  // Extract from temporary variables and find if the response is correct
  try {
    const fieldInlineGrader = _.get(stepInlineGrader, inlinePath);

    // Start with inline grader for multi correctness
    if (
      fieldInlineGrader &&
      fieldInlineGrader.isGraded &&
      fieldInlineGrader.validAnswers &&
      fieldInlineGrader.validAnswers.length > 0
    ) {
      if (fieldInlineGrader.validAnswers.includes(response)) return TutorialMessageType.SUCCESS;
    }

    // Fall back to normal 1:1 expected response if not in inline
    return _.isEqual(response, expectedAtIndex)
      ? TutorialMessageType.SUCCESS
      : TutorialMessageType.ERROR;
  } catch (e) {
    // If there is an error, return INFO
    return TutorialMessageType.INFO;
  }
};

const getTutorialResponseForFieldWrapper = ({
  fieldId,
  stepIndex,
  response,
  candidateId,
  expectedResponse,
  tutorialResponse,
}: {
  fieldId: string;
  stepIndex: number;
  response: $FIXME;
  candidateId?: string;
  expectedResponse?: $FIXME;
  tutorialResponse?: TutorialResponseType;
}) => {
  // If there are no responses, return null (probably in a task/non-tutorial bm)
  if (!tutorialResponse && !expectedResponse) {
    return null;
  }
  // Otherwise go to tutorial logic
  else if (
    tutorialResponse &&
    doesFieldHaveInstructions({ fieldId, stepIndex, tutorialResponse, candidateId })
  ) {
    try {
      const message = getTutorialMessageForField({
        fieldId,
        stepIndex,
        tutorialResponse,
        candidateId,
      });
      const messageType = getTutorialMessageTypeForField({
        fieldId,
        stepIndex,
        response,
        candidateId,
        expectedResponse,
        tutorialResponse,
      });
      const highlight = getTutorialHighlightForField({
        fieldId,
        stepIndex,
        tutorialResponse,
        candidateId,
      });

      return {
        message,
        messageType,
        highlight,
      };
    } catch (e) {
      return null;
    }
  }
  // If there are responses but no tutorial response for field, return null
  return null;
};

const initialState = {
  tutorialResponse: undefined,
  expectedResponse: undefined,
  tutorialMode: BenchmarkTutorialMode.Course,
  highlightStrings: undefined,
  highlightActive: false,
  highlightFieldId: undefined,
  highlightType: undefined,
  canSubmitScenario: false,
  acknowledgedTutorialFields: {},
  canSubmitCourseBenchmark: false,
  handleSubmitCourseBenchmark: undefined,
};

export const useTutorialStore = create<TutorialStoreType>()(
  devtools(
    (set, get) => ({
      ...initialState,
      setTutorialResponse: (tutorialResponse: TutorialResponseType | undefined) => {
        set({ tutorialResponse }, false, 'setTutorialResponse');
      },
      setExpectedResponse: (expectedResponse: $FIXME) => {
        set({ expectedResponse }, false, 'setExpectedResponse');
      },
      getTutorialResponseForField: (
        fieldId: string,
        stepIndex: number,
        response: $FIXME,
        candidateId?: string,
      ) => {
        const { tutorialResponse, expectedResponse } = get();
        return getTutorialResponseForFieldWrapper({
          fieldId,
          stepIndex,
          response,
          candidateId,
          tutorialResponse,
          expectedResponse,
        });
      },
      setHighlightStrings: (highlightStrings: string[] | undefined) => {
        set({ highlightStrings }, false, 'setHighlightStrings');
      },
      setHighlightActive: (highlightActive: boolean) => {
        set({ highlightActive }, false, 'setHighlightActive');
      },
      setHighlightFieldId: (highlightFieldId: string | undefined) => {
        set({ highlightFieldId }, false, 'setHighlightFieldId');
      },
      setHighlightType: (highlightType: TutorialMessageType | undefined) => {
        set({ highlightType }, false, 'setHighlightType');
      },
      setTutorialMode: (tutorialMode: BenchmarkTutorialMode) => {
        set({ tutorialMode }, false, 'setTutorialMode');
      },
      setCanSubmitScenario: (canSubmitScenario: boolean) => {
        set({ canSubmitScenario }, false, 'setCanSubmitScenario');
      },
      setAcknowledgedTutorialFields: (acknowledgedTutorialFields: Record<string, boolean>) => {
        set({ acknowledgedTutorialFields }, false, 'setAcknowledgedTutorialFields');
      },
      setCanSubmitCourseBenchmark: (canSubmitCourseBenchmark: boolean) => {
        set({ canSubmitCourseBenchmark }, false, 'setCanSubmitCourseBenchmark');
      },
      setHandleSubmitCourseBenchmark: (
        handleSubmitCourseBenchmark:
          | ((
              sendType?: SendTypes,
              isReportedForSensitiveContent?: boolean,
              sensitiveContentReportReason?: string,
              sensitiveContentReportExplanation?: string,
            ) => Promise<void>)
          | undefined,
      ) => {
        set({ handleSubmitCourseBenchmark }, false, 'setHandleSubmitCourseBenchmark');
      },
    }),
    {
      name: 'tutorial-store',
    },
  ),
);
